import { Logout } from '@mui/icons-material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { Avatar, Box, Button, Divider, IconButton, Stack, Tab, Tabs, Typography, alpha } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, Theme, styled, useTheme } from '@mui/material/styles';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { USER_ROLES } from '../all-types';
import { STYLE_CONSTANTS } from '../constants';
import { useAppSelector } from '../redux-store/hooks';
import { getSideBarByRole } from '../role-manager';

const openedMixin = (theme: Theme): CSSObject => ({
  width: STYLE_CONSTANTS.DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  backgroundColor: theme.palette.background.paper,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 0,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: STYLE_CONSTANTS.DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface SideBarProps {
  open: boolean;
  handleDrawerClose: () => void;
  toggleTheme: () => void;
}

const SideBar: FC<SideBarProps> = ({ open, handleDrawerClose, toggleTheme }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const themeMode = theme.palette.mode;
  const client = useAppSelector((state) => state.clientInfo.data);
  const auth = useAppSelector((state) => state.authentication);
  const sidebarUrls = getSideBarByRole(auth.role as USER_ROLES);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const key = Object.keys(sidebarUrls)[newValue];
    navigate(sidebarUrls[key].url);
  };

  return (
    <Drawer variant="permanent" open={open}>
      {/* --- LOGO SECTION --- */}
      <Box sx={{
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      }}>
        <Avatar
          src={client?.clientLogo}
          onClick={() => handleDrawerClose()}
          sx={{
            width: 100,
            height: 100,
            borderRadius:"0",
            cursor: 'pointer',
            transition: 'transform 0.3s ease',
            '&:hover': { transform: 'scale(1.05)' },
            bgcolor: 'transparent'
          }}
        />
      </Box>

      {/* --- MENU TABS --- */}
      <Box sx={{
        flex: 1,
        px: 2,
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.primary.main, 0.2),
          borderRadius: '10px',
        }
      }}>
        <Tabs
          orientation="vertical"
          value={selectedTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ sx: { display: 'none' } }} // Hide default indicator
          sx={{
            '& .MuiTabs-flexContainer': { gap: 1 }
          }}
        >
          {Object.keys(sidebarUrls).map((key, index) => (
            <Tab
              key={key}
              icon={sidebarUrls[key].icon}
              label={sidebarUrls[key].name}
              iconPosition="top"
              sx={{
                borderRadius: '16px',
                minHeight: 80,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                color: theme.palette.text.secondary,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.50),
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* --- FOOTER SECTION --- */}
      <Stack p={2} spacing={2} alignItems="center">
        
        {/* Modern Theme Toggle */}
        <Box
          onClick={toggleTheme}
          sx={{
            width: 70,
            height: 32,
            borderRadius: 10,
            bgcolor: alpha(theme.palette.text.primary, 0.05),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: 'flex',
            alignItems: 'center',
            px: 0.5,
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: themeMode === 'dark' ? 'primary.main' : 'common.white',
              boxShadow: theme.shadows[2],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: themeMode === 'dark' ? 'translateX(38px)' : 'translateX(0)',
            }}
          >
            {themeMode === 'dark' ? <Brightness4Icon sx={{ fontSize: 16, color: '#fff' }} /> : <Brightness7Icon sx={{ fontSize: 16, color: 'warning.main' }} />}
          </Box>
        </Box>

        <Divider sx={{ width: '80%', opacity: 0.5 }} />

        {/* User Profile Card */}
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={2} 
          sx={{ 
            width: '100%', 
            p: 1.5, 
            borderRadius: '16px',
            bgcolor: alpha(theme.palette.error.main, 0.03) 
          }}
        >
          <Avatar 
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: 'primary.main', 
              fontSize: '0.8rem', 
              fontWeight: 700 
            }}
          >
            {auth?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.8rem' }}>
              {auth?.username}
            </Typography>
            {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Online
            </Typography> */}
          </Box>
        </Stack>
          <IconButton 
            size="small" 
            color="error" 
            onClick={() => {
              localStorage.clear();
              window.location.assign("/");
            }}
            sx={{ 
                bgcolor: alpha(theme.palette.error.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
            }}
          >
            <Logout sx={{ fontSize: 18 }} />
          </IconButton>

        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px' }}>
          VER 2.26.2.18
        </Typography>
      </Stack>
    </Drawer>
  );
};

export default SideBar;